import React, { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CalendarIcon, DollarSign, AlertCircle, Bot, CheckCircle } from 'lucide-react';
import { useExpenseInvoiceUpload } from '@/hooks/useExpenses';
import { format } from 'date-fns';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Vehicle } from '@/hooks/vehicle-maintenance/types';
import { ExpenseCategory, ExpenseFormValues } from './types';
import type { OCRProcessingResult } from '@/services/expense/ExpenseService';
import { ReceiptUploadZone } from './ReceiptUploadZone';

const expenseCategories: ExpenseCategory[] = [
  'fuel',
  'maintenance',
  'repair',
  'parts',
  'tires',
  'insurance',
  'registration',
  'toll',
  'parking',
  'accommodation',
  'camping',
  'equipment',
  'other',
];

const currencies = [
  'USD',
  'EUR',
  'GBP',
  'CAD',
  'AUD',
  'JPY',
  'CNY',
  'ZAR',
  'INR',
];

const expenseSchema = z.object({
  vehicle_id: z.string().uuid('Please select a vehicle'),
  amount: z.number().positive('Amount must be greater than 0'),
  currency: z.string().min(1, 'Please select a currency'),
  category: z.string().min(1, 'Please select a category'),
  description: z.string().min(3, 'Description must be at least 3 characters'),
  expense_date: z.date(),
  notes: z.string().optional(),
});

interface ExpenseFormProps {
  vehicles: Vehicle[];
  onSubmit: (data: ExpenseFormValues, files: File[]) => Promise<void>;
  onCancel: () => void;
  isUpdate?: boolean;
  initialValues?: Partial<ExpenseFormValues>;
}

export const ExpenseForm: React.FC<ExpenseFormProps> = ({
  vehicles,
  onSubmit,
  onCancel,
  isUpdate = false,
  initialValues,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [ocrData, setOcrData] = useState<OCRProcessingResult['invoiceData'] | null>(null);
  const [isOcrProcessing, setIsOcrProcessing] = useState(false);
  const [ocrSuccess, setOcrSuccess] = useState(false);

  const { uploadInvoice, uploadProgress } = useExpenseInvoiceUpload();

  const form = useForm<ExpenseFormValues>({
    resolver: zodResolver(expenseSchema),
    defaultValues: {
      vehicle_id: initialValues?.vehicle_id || '',
      amount: initialValues?.amount || 0,
      currency: initialValues?.currency || 'AUD',
      category: initialValues?.category || 'maintenance',
      description: initialValues?.description || '',
      expense_date: initialValues?.expense_date || new Date(),
      notes: initialValues?.notes || '',
    },
  });

  // Handle OCR processing when files are added
  const handleFilesChange = async (files: File[]) => {
    setUploadFiles(files);

    // If a receipt/invoice file is uploaded, try OCR processing
    const receiptFile = files.find(file =>
      file.type === 'application/pdf' || file.type.startsWith('image/')
    );

    if (receiptFile && !ocrSuccess) {
      setIsOcrProcessing(true);
      setOcrData(null);

      try {
        const result = await uploadInvoice(receiptFile, 'google_vision');

        if (result.ocrResult.success && result.ocrResult.invoiceData) {
          const data = result.ocrResult.invoiceData;
          setOcrData(data);
          setOcrSuccess(true);

          // Auto-populate form fields with OCR data
          if (data.totalAmount) {
            form.setValue('amount', data.totalAmount);
          }
          if (data.vendorName) {
            form.setValue('description', `${data.vendorName} - ${data.invoiceNumber || 'Receipt'}`);
          }
          if (data.invoiceDate) {
            form.setValue('expense_date', new Date(data.invoiceDate));
          }
          if (data.currency) {
            form.setValue('currency', data.currency);
          }
        }
      } catch (error) {
        console.warn('OCR processing failed, allowing manual entry:', error);
      } finally {
        setIsOcrProcessing(false);
      }
    }
  };

  const handleSubmit = async (data: ExpenseFormValues) => {
    if (!isUpdate && uploadFiles.length === 0) {
      setSubmitError('Please upload at least one receipt or invoice');
      return;
    }

    setSubmitError(null);
    setIsSubmitting(true);

    try {
      await onSubmit(data, uploadFiles);
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : 'Failed to save expense'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="h-5 w-5" />
          {isUpdate ? 'Update Expense' : 'Record Expense'}
        </CardTitle>
        <CardDescription>
          Track your Unimog expenses with receipts and documents
        </CardDescription>
      </CardHeader>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <CardContent className="space-y-6">
            {submitError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="vehicle_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Vehicle</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      disabled={isUpdate}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a vehicle" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {vehicles.map((vehicle) => (
                          <SelectItem key={vehicle.id} value={vehicle.id}>
                            {vehicle.name} ({vehicle.model} {vehicle.year})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {expenseCategories.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category.charAt(0).toUpperCase() +
                              category.slice(1).replace(/_/g, ' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amount</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Currency</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {currencies.map((currency) => (
                          <SelectItem key={currency} value={currency}>
                            {currency}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="expense_date"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className="pl-3 text-left font-normal"
                          >
                            {field.value
                              ? format(field.value, 'MMM d, yyyy')
                              : 'Pick a date'}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) =>
                            date > new Date() || date < new Date('1900-01-01')
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., Oil change at local service center"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    What was this expense for?
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Additional details about this expense..."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!isUpdate && (
              <div className="space-y-4">
                <FormLabel className="mb-3 block">
                  Upload Receipts/Invoices
                </FormLabel>

                {/* OCR Status Display */}
                {isOcrProcessing && (
                  <Alert>
                    <Bot className="h-4 w-4 animate-spin" />
                    <AlertDescription>
                      Reading receipt automatically... ({uploadProgress}%)
                    </AlertDescription>
                  </Alert>
                )}

                {ocrSuccess && ocrData && (
                  <Alert className="border-green-200 bg-green-50">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-800">
                      Receipt read successfully! Form fields have been auto-filled.
                      <br />
                      <span className="text-sm text-green-600">
                        Vendor: {ocrData.vendorName} • Amount: {ocrData.currency} {ocrData.totalAmount}
                      </span>
                    </AlertDescription>
                  </Alert>
                )}

                <ReceiptUploadZone
                  files={uploadFiles}
                  onFilesChange={handleFilesChange}
                  maxFiles={10}
                  disabled={isOcrProcessing}
                />
              </div>
            )}
          </CardContent>

          <CardFooter className="flex justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || isOcrProcessing}>
              {isOcrProcessing
                ? 'Reading Receipt...'
                : isSubmitting
                  ? 'Processing...'
                  : isUpdate
                    ? 'Update Expense'
                    : 'Record Expense'}
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
};
